import type { BrandSpaceApplicationDeliveryStatus } from '@fluenceur/types/brand-space';
import { Badge, Flex, Text } from '@chakra-ui/react';
import { CheckCircle, InfoCircle, WarningTriangle } from 'iconoir-react';
import { useTranslation } from 'react-i18next';

/**
 * How a transactional message fared, as the brand reads it.
 *
 * Shared on purpose between the Brand Space applications screen and the
 * invitations screen: it is the same message, the same provider callbacks and
 * the same brand. Two screens describing one reality with two vocabularies
 * would be a regression, so the wording lives here once.
 *
 * The vocabulary keeps the translation namespace it was first written under
 * (`delivery.*`) rather than being copied to a second
 * one — a copy is exactly the drift this component exists to prevent.
 */
export const DELIVERY_STATUS_TRANSLATION_PREFIX = 'delivery';

/**
 * Every real `BrandSpaceApplicationDeliveryStatus`, plus `reused` for an
 * invitation that already existed and needed no new message (derived by the
 * caller, never sent by the API), plus `unavailable` — the safe fallback for a
 * value this build does not recognize. Native `unknown` means a real send
 * attempt has an uncertain outcome, which is distinct from an unreadable status.
 */
export type DisplayDeliveryStatus = BrandSpaceApplicationDeliveryStatus | 'reused' | 'unavailable';

/**
 * A rebound or a spam complaint means the creator never got the message —
 * exactly like an outright send failure, and unlike a message still in
 * flight. `suppressed` is the same verdict reached before sending. All of them
 * must read as a problem, never as "still pending".
 */
const FAILURE_STATUSES: ReadonlySet<DisplayDeliveryStatus> = new Set([
  'permanent_failure',
  'suppressed',
  'bounced',
  'complained',
]);

/**
 * `delivered` and `clicked` are provider/creator confirmations arriving after
 * `sent` — per the backend's own contract they only ever refine `sent` and
 * never regress it, so they read as "done" exactly like `sent` and `reused`
 * already do.
 */
const COMPLETE_STATUSES: ReadonlySet<DisplayDeliveryStatus> = new Set([
  'sent',
  'reused',
  'delivered',
  'clicked',
]);

/** Every status this badge knows how to translate. Anything else falls back to `unavailable`. */
export const KNOWN_DELIVERY_STATUSES: ReadonlySet<DisplayDeliveryStatus> = new Set([
  'not_required',
  'pending',
  'processing',
  'unknown',
  'sent',
  'suppressed',
  'skipped_non_production',
  'permanent_failure',
  'delivered',
  'clicked',
  'bounced',
  'complained',
  'reused',
]);

/**
 * Narrows whatever arrived over the wire to a status this build can name.
 * Defends against a value the type system already rules out, which is the
 * point: a corrupt row or a newer backend must never surface a raw key.
 */
export function toDisplayDeliveryStatus(status: string | null | undefined): DisplayDeliveryStatus {
  return KNOWN_DELIVERY_STATUSES.has(status as DisplayDeliveryStatus)
    ? (status as DisplayDeliveryStatus)
    : 'unavailable';
}

export function DeliveryStatusBadge({
  status,
  attemptCount,
}: {
  status: DisplayDeliveryStatus;
  /** Transport attempts to name under the badge. Omitted or zero shows nothing. */
  attemptCount?: number | null;
}) {
  const { t } = useTranslation();

  const failure = FAILURE_STATUSES.has(status);
  const complete = COMPLETE_STATUSES.has(status);
  const Icon = failure ? WarningTriangle : complete ? CheckCircle : InfoCircle;
  const showAttempts = typeof attemptCount === 'number' && attemptCount > 0;

  return (
    <Flex direction="column" align="flex-start" gap="3px">
      <Badge colorPalette={failure ? 'red' : complete ? 'green' : 'orange'} variant="subtle">
        <Icon />
        {t(`${DELIVERY_STATUS_TRANSLATION_PREFIX}.${status}`)}
      </Badge>
      {showAttempts ? (
        <Text fontSize="10px" color="inkSubtle">
          {t(`${DELIVERY_STATUS_TRANSLATION_PREFIX}.attempts`, { count: attemptCount })}
        </Text>
      ) : null}
    </Flex>
  );
}
